import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, MessageSquare, MessageCircle } from "lucide-react";
import { COMPANY, LOGO_URL, NAV_ITEMS } from "../mock";
import CallbackDialog from "./CallbackDialog";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header className={`pt-header sticky top-0 z-40 ${scrolled ? "scrolled" : ""}`}>
        <div className="max-w-[1320px] mx-auto px-6 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center shrink-0 py-3">
            <img src={LOGO_URL} alt={COMPANY.name} className="h-[72px] w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="pt-nav-item relative">
                <NavLink
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) => `pt-nav-link ${isActive ? "active" : ""}`}
                >
                  {item.label}
                  {item.children && <ChevronDown size={13} className="ml-1 opacity-60" />}
                </NavLink>
                {item.children && (
                  <div className="pt-dropdown">
                    {item.children.map((c) => (
                      <Link key={c.path} to={c.path}>{c.label}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden md:flex items-center">
            <CallbackDialog>
              <button className="btn-gold">
                <Phone size={14} />
                Submit application
              </button>
            </CallbackDialog>
          </div>

          <button
            onClick={() => setMobileOpen((s) => !s)}
            className="lg:hidden p-2 text-[#16223a]"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="pt-mobile-menu lg:hidden">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="border-b border-[#e6e2d6]">
                <button
                  onClick={() => setOpenSub(openSub === item.label ? null : item.label)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left font-medium text-sm"
                >
                  <Link to={item.path} onClick={() => setMobileOpen(false)}>{item.label}</Link>
                  {item.children && <ChevronDown size={16} className={`transition-transform ${openSub === item.label ? "rotate-180" : ""}`} />}
                </button>
                {item.children && openSub === item.label && (
                  <div className="bg-[#faf7ef] py-2">
                    {item.children.map((c) => (
                      <Link key={c.path} to={c.path} onClick={() => setMobileOpen(false)} className="!border-0 !py-2 pl-10 text-sm">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="p-6 space-y-3 text-sm text-[#4a4a4a]">
              <div className="flex items-center gap-2"><Phone size={14} className="text-[#a98a3f]" />{COMPANY.phonePrimary}</div>
              <div className="flex items-center gap-2"><Mail size={14} className="text-[#a98a3f]" />{COMPANY.email}</div>
            </div>
            <div className="p-6">
              <CallbackDialog>
                <button className="btn-gold w-full justify-center">
                  <Phone size={14} /> Submit application
                </button>
              </CallbackDialog>
            </div>
          </div>
        )}
      </header>

      {/* Side floating action buttons */}
      <div className="pt-side-actions hidden md:flex">
        <a href={`tel:${COMPANY.phonePrimary.replace(/\s/g, "")}`} title="Call us">
          <Phone size={18} />
        </a>
        <a
          href="https://wa.me/447774489777"
          target="_blank"
          rel="noopener noreferrer"
          title="WhatsApp"
          className="!text-[#25D366] hover:!bg-[#25D366] hover:!text-white"
        >
          <MessageCircle size={18} />
        </a>
        <CallbackDialog>
          <button title="Request callback">
            <MessageSquare size={18} />
          </button>
        </CallbackDialog>
        <a href={`mailto:${COMPANY.email}`} title="Email us">
          <Mail size={18} />
        </a>
      </div>

      {/* Floating WhatsApp button (mobile + desktop) */}
      <a
        href="https://wa.me/447774489777"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 left-5 z-[36] w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:bg-[#1EBE57] hover:scale-105 transition-all"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </>
  );
};

export default Header;
